import "./App.css";
import "./responsive.css";
import "bootstrap/dist/css/bootstrap.min.css";
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  NavLink,
} from "react-router-dom";
import {
  LuCalendarRange,
  LuChartColumnBig,
  LuPanelLeftOpen,
  LuSettings2,
} from "react-icons/lu";
import LoginLayout from "./pages/LoginLayout";
import { ToastContainer } from "react-toastify";
import BrandLogo from "./components/BrandLogo";
import { BRAND_NAME } from "./branding";

const AppRouter = lazy(() => import("./router/Routes"));
const User = lazy(() => import("./layout/header/dropdown/user/User"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const RegisterSuccess = lazy(() => import("./pages/auth/RegisterSuccess"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const Success = lazy(() => import("./pages/auth/Success"));

const PageFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
    <div className="page-fallback">
      <div className="page-fallback-row short" />
      <div className="page-fallback-row" />
      <div className="page-fallback-card" />
      <div className="page-fallback-card" />
    </div>
  </div>
);

function App() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [sidebarOpen]);

  const links = [
    { to: "/user/connections", label: "Connections" },
    { to: "/user/select-reports", label: "Select Reports" },
    { to: "/user/start-sync", label: "Start Sync" },
  ];
  const isSettingsActive = links.some((link) =>
    location.pathname.startsWith(link.to)
  );
  const noSidebarRoutes = [
    "/",
    "/auth-success",
    "/auth-reset",
    "/auth-resetPassword",
    "/auth-register",
    "/auth-register-success",
    "/auth-login",
  ];

  const hideSidebarAndHeader = noSidebarRoutes.includes(location.pathname);
  const routeMeta = [
    {
      match: (path) => path === "/dashboard",
      eyebrow: "Overview",
      title: "Dashboard",
      subtitle: "Monitor report sync activity and recent client delivery status.",
    },
    {
      match: (path) => path === "/ScheduledReports",
      eyebrow: "Exports",
      title: "Amazon Reports",
      subtitle: "Export Amazon Ads campaign data to Excel for a selected date range.",
    },
    {
      match: (path) => path === "/knowledge" || path === "/playbooks",
      eyebrow: "Knowledge",
      title: "Knowledge Library",
      subtitle: `Capture playbooks, SOPs, policies, and historical cases that ${BRAND_NAME} can cite in recommendations.`,
    },
    {
      match: (path) => path === "/knowledge/playbooks",
      eyebrow: "Knowledge",
      title: "Playbooks",
      subtitle: "Create and manage structured optimization logic for repeatable decisions.",
    },
    {
      match: (path) => path === "/knowledge/sops",
      eyebrow: "Knowledge",
      title: "SOPs",
      subtitle: "Manage operational procedures, review cadences, and escalation guidance.",
    },
    {
      match: (path) => path === "/knowledge/best-practices",
      eyebrow: "Knowledge",
      title: "Best Practices",
      subtitle: "Store reusable guidance from Amazon documentation, agency standards, and expert operators.",
    },
    {
      match: (path) => path === "/knowledge/historical-cases",
      eyebrow: "Knowledge",
      title: "Historical Cases",
      subtitle: "Capture past optimization decisions, results, and learning that the AI can cite as precedent.",
    },
    {
      match: (path) => path === "/knowledge/client-policies",
      eyebrow: "Knowledge",
      title: "Client Policies",
      subtitle: "Capture client-specific rules, non-negotiables, and approval constraints.",
    },
    {
      match: (path) => path === "/recommendations",
      eyebrow: "Approvals",
      title: "Recommendations",
      subtitle: "Review AI-generated decisions, inspect supporting evidence, and approve actions safely.",
    },
    {
      match: (path) => path.startsWith("/user/"),
      eyebrow: "Settings",
      title: "Amazon Connections",
      subtitle: "Configure Ads, Seller, and Vendor account access without changing workflows.",
    },
  ];
  const currentRouteMeta =
    routeMeta.find((item) => item.match(location.pathname)) ??
    {
      eyebrow: "Workspace",
      title: BRAND_NAME,
      subtitle: "Explainable optimization operations for Amazon Ads agencies.",
    };

  return (
    <>
      <ToastContainer />
      {!hideSidebarAndHeader ? (
        <div className="d-flex flex-column app-container">
          <div className="d-flex flex-grow-1">
            {sidebarOpen && (
              <button
                type="button"
                className="sidebar-overlay"
                aria-label="Close navigation"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <aside
              className={`sidebar${sidebarOpen ? " sidebar-open" : ""}`}
              aria-label="Main navigation"
            >
              <NavLink to="/dashboard" className="sidebar-brand">
                <BrandLogo className="sidebar-logo" />
                <span className="sidebar-brand-text">{BRAND_NAME}</span>
              </NavLink>
              <hr className="sidebar-divider" />
              <nav>
                <ul className="list-unstyled mt-4">
                  <li>
                    <NavLink to="/dashboard" className="sidebar-link" end>
                      <LuChartColumnBig />
                      <span>Dashboard</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink to="/ScheduledReports" className="sidebar-link">
                      <LuCalendarRange />
                      <span>Amazon Reports</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/user/connections"
                      className={`sidebar-link${isSettingsActive ? " active" : ""}`}
                    >
                      <LuSettings2 />
                      <span>Settings</span>
                    </NavLink>
                  </li>
                </ul>
              </nav>
            </aside>

            <div className="main-content flex-grow-1">
              <header className="header">
                <div className="header-content">
                  <button
                    type="button"
                    className="sidebar-toggle-btn d-lg-none"
                    aria-label="Open navigation"
                    aria-expanded={sidebarOpen}
                    onClick={() => setSidebarOpen((open) => !open)}
                  >
                    <LuPanelLeftOpen size={20} aria-hidden="true" />
                  </button>
                  <div className="header-copy">
                    <div className="header-eyebrow">{currentRouteMeta.eyebrow}</div>
                    <h1 className="header-title">{currentRouteMeta.title}</h1>
                    <p className="header-subtitle">{currentRouteMeta.subtitle}</p>
                  </div>
                </div>
                <div className="header-actions">
                  <Suspense fallback={null}>
                    <User />
                  </Suspense>
                </div>
              </header>

              <div className="page-shell">
                <Suspense fallback={<PageFallback />}>
                  <AppRouter />
                </Suspense>
              </div>
            </div>
          </div>

          <footer className="app_footer">
            <p style={{ margin: "0", fontSize: "12px" }}>
              &copy; 2026 {BRAND_NAME}. All Rights Reserved.
            </p>
          </footer>
        </div>
      ) : (
        <LoginLayout>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/" element={<Navigate to="/auth-login" replace />} />
              <Route path="/auth-login" element={<Login />} />
              <Route path="/auth-register" element={<Register />} />
              <Route
                path="/auth-register-success"
                element={<RegisterSuccess />}
              />
              <Route path="/auth-reset" element={<ForgotPassword />} />
              <Route path="/auth-success" element={<Success />} />
            </Routes>
          </Suspense>
        </LoginLayout>
      )}
    </>
  );
}

export default App;
